import { toolsData } from './toolsData.js';
import { translations } from '../utils/translations.js';

export const SITE_URL = 'https://3toolspro.com';

export const getCategorySlug = (category) =>
  category.toLowerCase().replace(/&/g, 'and').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

export const getToolSlugFromPath = (path) => path.replace('/tools/', '');

export const getToolGuidePath = (toolPath, language = 'en') => `/articles/${language}-${getToolSlugFromPath(toolPath)}-guide`;

const toSentenceCase = (text) => text.charAt(0).toUpperCase() + text.slice(1);

const getCategoryFocus = (category) => {
  const categoryMap = {
    'Text & Content': 'content writing, text editing, and SEO formatting',
    'Image Tools': 'image editing, format conversion, and asset optimization',
    'PDF Tools': 'PDF editing, page management, and file optimization',
    'Calculator Tools': 'everyday calculation, finance, and date math',
    'Text Tools': 'developer-friendly text cleanup and formatting',
    Developer: 'developer workflows, encoding, and formatting',
    Utility: 'daily productivity and randomization tasks',
  };

  return categoryMap[category] || 'online productivity workflows';
};

const getGeneralFaqs = (toolName) => [
  {
    question: `Is the ${toolName} free to use?`,
    answer: `Yes. The ${toolName} is available for free and runs directly in your browser with no account required.`,
  },
  {
    question: `Can I use the ${toolName} on mobile?`,
    answer: `Yes. The layout is responsive and optimized for mobile, tablet, and desktop screens.`,
  },
  {
    question: `Do I need to install any software for ${toolName}?`,
    answer: `No installation is needed. Open the tool page and start using it instantly in your browser.`,
  },
];

const buildToolArticle = (tool) => {
  const categoryFocus = getCategoryFocus(tool.category);
  const toolSlug = getToolSlugFromPath(tool.path);

  return {
    slug: `${toolSlug}-guide`,
    title: `${tool.name} Guide: How to Use It Effectively`,
    description: `Learn what ${tool.name} does, how to use it step by step, key benefits, frequently asked questions, and related tools for faster workflows.`,
    category: tool.category,
    publishedAt: '2026-02-12',
    toolPath: tool.path,
    relatedToolIds: toolsData
      .filter((item) => item.category === tool.category && item.id !== tool.id)
      .slice(0, 4)
      .map((item) => item.id),
    keywords: [
      `${tool.name.toLowerCase()} online`,
      `${tool.name.toLowerCase()} free`,
      `${tool.category.toLowerCase()} tools`,
      `${toolSlug.replace(/-/g, ' ')} guide`,
      'browser-based tools',
      'responsive web tool',
    ],
    sections: {
      whatIs: `The ${tool.name} is a browser-based utility designed for ${categoryFocus}. It helps you complete tasks quickly without installing software and works smoothly across modern devices.`,
      howToUse: [
        `Open the ${tool.name} tool page from the tools directory.`,
        `Add or configure the input needed for your ${tool.name.toLowerCase()} task.`,
        `Review the generated output and make any quick adjustments as needed.`,
        `Copy, download, or reuse the final result in your workflow.`,
      ],
      benefits: [
        `Saves time by handling ${tool.name.toLowerCase()} tasks directly in the browser.`,
        `No account or software setup required for faster onboarding.`,
        `Mobile-friendly layout for editing and checking results anywhere.`,
      ],
      faqs: getGeneralFaqs(tool.name),
    },
  };
};

export const toolCategories = Array.from(new Set(toolsData.map((tool) => tool.category))).map((category) => ({
  name: category,
  slug: getCategorySlug(category),
  description: `Explore the best ${category} to speed up your workflow with fast, browser-based utilities.`,
}));

export const baseArticlesData = toolsData.map((tool) => buildToolArticle(tool));

const articleLanguageTemplates = {
  en: {
    guideTitle: '{tool} Guide: How to Use It Effectively',
    guideDescription: 'Learn what {tool} does, how to use it step by step, key benefits, frequently asked questions, and related tools for faster workflows.',
  },
  ar: {
    guideTitle: 'دليل {tool}: كيفية الاستخدام بشكل فعّال',
    guideDescription: 'تعرّف على وظيفة أداة {tool} وطريقة استخدامها خطوة بخطوة وأهم الفوائد والأسئلة الشائعة والأدوات المرتبطة لتسريع العمل.',
  },
  fr: {
    guideTitle: "Guide {tool} : comment l'utiliser efficacement",
    guideDescription: "Découvrez à quoi sert {tool}, comment l'utiliser étape par étape, ses avantages clés, les FAQ et les outils associés.",
  },
  es: {
    guideTitle: 'Guía de {tool}: cómo usarla de forma eficaz',
    guideDescription: 'Aprende qué hace {tool}, cómo usarla paso a paso, sus beneficios clave, preguntas frecuentes y herramientas relacionadas.',
  },
  de: {
    guideTitle: '{tool}-Anleitung: So nutzt du es effektiv',
    guideDescription: 'Erfahre, was {tool} macht, wie du es Schritt für Schritt nutzt, welche Vorteile es bietet und welche Tools dazu passen.',
  },
  zh: {
    guideTitle: '{tool} 使用指南：高效上手',
    guideDescription: '了解 {tool} 的功能、分步使用方法、核心优势、常见问题以及相关工具。',
  },
  pt: {
    guideTitle: 'Guia do {tool}: como usar com eficiência',
    guideDescription: 'Veja o que o {tool} faz, como usar passo a passo, principais benefícios, FAQ e ferramentas relacionadas.',
  },
};

const renderTemplate = (template, params) =>
  Object.keys(params).reduce((acc, key) => acc.replaceAll(`{${key}}`, params[key]), template);

const getLocalizedToolName = (toolPath, language) => {
  const tool = toolsData.find((item) => item.path === toolPath);
  if (!tool) return '';
  const key = `tool${tool.component}`;
  return translations[language]?.[key] || translations.en?.[key] || tool.name;
};

export const getLocalizedArticlesData = (language = 'en') =>
  baseArticlesData.map((article) => {
    const template = articleLanguageTemplates[language] || articleLanguageTemplates.en;
    const localizedToolName = getLocalizedToolName(article.toolPath, language);

    return {
      ...article,
      language,
      title: renderTemplate(template.guideTitle, { tool: localizedToolName }),
      description: renderTemplate(template.guideDescription, { tool: localizedToolName }),
      slug: `${language}-${article.slug}`,
      sourceSlug: article.slug,
    };
  });

export const articlesData = getLocalizedArticlesData('en');

export const getArticleBySlug = (slug, language = 'en') =>
  getLocalizedArticlesData(language).find((item) => item.slug === slug);

export const getArticleByToolPath = (toolPath, language = 'en') =>
  getLocalizedArticlesData(language).find((item) => item.toolPath === toolPath);

export const getRelatedArticles = (article, language = 'en', maxItems = 4) => {
  const localizedArticles = getLocalizedArticlesData(language);
  const sameCategoryArticles = localizedArticles.filter(
    (item) => item.slug !== article.slug && item.category === article.category
  );

  if (sameCategoryArticles.length >= maxItems) {
    return sameCategoryArticles.slice(0, maxItems);
  }

  const fallbackArticles = localizedArticles.filter(
    (item) => item.slug !== article.slug && item.category !== article.category
  );

  return [...sameCategoryArticles, ...fallbackArticles].slice(0, maxItems);
};

export const contentCopy = {
  en: {
    articleNotFound: 'Article not found',
    backToArticles: 'Back to articles',
    articleHubTitle: 'Tool Guides & Articles',
    articleHubSubtitle:
      'Step-by-step guides for every tool covering usage, benefits, FAQs, and related resources.',
    readArticle: 'Read article →',
    whatIsIt: 'What is it',
    howToUse: 'How to use',
    benefits: 'Benefits',
    faqs: 'FAQs',
    relatedTools: 'Related tools',
    relatedGuides: 'Related guides',
    exploreCategory: 'Explore',
    seoKeywords: 'SEO keywords',
  },
  ar: {
    articleNotFound: 'المقال غير موجود',
    backToArticles: 'العودة إلى المقالات',
    articleHubTitle: 'أدلة الأدوات والمقالات',
    articleHubSubtitle:
      'أدلة خطوة بخطوة لكل أداة تشمل طريقة الاستخدام، الفوائد، الأسئلة الشائعة، والموارد المرتبطة.',
    readArticle: 'قراءة المقال ←',
    whatIsIt: 'ما هي الأداة',
    howToUse: 'طريقة الاستخدام',
    benefits: 'الفوائد',
    faqs: 'الأسئلة الشائعة',
    relatedTools: 'أدوات مرتبطة',
    relatedGuides: 'أدلة مرتبطة',
    exploreCategory: 'استكشف',
    seoKeywords: 'كلمات مفتاحية SEO',
  },
};


const completeContentCopy = Object.fromEntries(
  Object.keys(translations).map((lang) => [lang, { ...contentCopy.en, ...(contentCopy[lang] || {}) }])
);

export const getContentCopy = (language) => completeContentCopy[language] || contentCopy.en;


export const getArticleLinkLabel = (article) => toSentenceCase(article.title);
